import { Dialog as DialogPrimitive } from 'radix-ui';
import { XIcon } from '@music-vine/cadence/icons';
import { Badge, Button, Dialog, DialogClose, DialogOverlay, DialogPortal, DialogTitle, Drawer, DrawerContent } from '@music-vine/cadence/ui';
import Image from 'next/image';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MobileBanner } from '@/components/PagePopup/MobileBanner';
import { setAsDismissedInStorage } from '@/components/PagePopup/utils/setAsDismissedInStorage';
import { useAnyPlayerActive } from '@/hooks/media/useAnyPlayerActive';
import useTWScreens from '@/hooks/ui/useTWScreens';
import { cn } from '@/lib/utils';
import type { Variants } from 'motion/react';
import type { ReactNode } from 'react';
const DIALOG_IMAGE_HEIGHT = 200;
const DISMISS_ANIMATION_DURATION_MS = 200;

// Minimum gap kept between the top of the drawer and the top of the visual
// viewport while the software keyboard is open.
const DRAWER_KEYBOARD_TOP_GAP = 24;

// Visual-viewport scale above which shrinkage is treated as pinch-zoom
// rather than the software keyboard opening.
const PINCH_ZOOM_SCALE_THRESHOLD = 1.01;
export interface PageDialogProps {
  children: ReactNode;
  /**
   * Delay in milliseconds before the dialogue shows
   */
  delay?: number;
  dismissPermanently?: boolean;
  /**
   * If true, the X icon for dismissing the dialogue will not be shown.
   */
  drawerChildren?: ReactNode;
  /**
   * Extra classes for the mobile drawer container, e.g. a max height
   * (`max-h-[90vh]`) — the drawer body scrolls when constrained.
   */
  drawerContentClassName?: string;
  duration?: {
    from: Date;
    to: Date;
  };
  excludeSlugs?: (string | RegExp)[];
  forceShow?: boolean;
  /**
   * An optional image rendered edge-to-edge at the top of the dialogue.
   */
  image?: {
    src: string;
    alt: string;
    /**
     * Optional pill rendered centred and straddling the image's bottom edge.
     */
    badge?: string;
  };
  /**
   * If provided, the dialogue only shows on pages whose path matches one of
   * these slugs (allow-list). Evaluated by `PageDialogQueue`.
   */
  includeSlugs?: (string | RegExp)[];
  isHidden?: boolean;
  /**
   * If provided, rendered on mobile as a compact bottom toast instead of the full dialogue.
   */
  mobileChildren?: ReactNode;
  noXIcon?: boolean;
  onClickDismiss?: () => void;
  /**
   * Called when the dialogue is shown to the user
   */
  onShow?: () => void;
  /**
   * Extra classes for the desktop dialogue panel, e.g. to widen it beyond the
   * default `max-w-[480px]`.
   */
  panelClassName?: string;
  revalidateKey?: string;
  storageKey: string;
  storageType?: 'localStorage' | 'sessionStorage';
  /**
   * Accessible name for the desktop dialogue, announced by screen readers.
   */
  title: string;
}
export const PageDialog = ({
  children,
  storageKey,
  storageType = 'localStorage',
  onClickDismiss,
  onShow,
  image,
  mobileChildren,
  drawerChildren,
  drawerContentClassName,
  noXIcon = false,
  delay = 1000,
  dismissPermanently = true,
  panelClassName,
  title
}: Omit<PageDialogProps, 'excludeSlugs' | 'includeSlugs' | 'duration'>) => {
  const {
    isMd
  } = useTWScreens();
  const anyPlayerActive = useAnyPlayerActive();
  const [show, setShow] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const drawerContentRef = useRef<HTMLDivElement>(null);

  // Vaul's built-in keyboard handling (`repositionInputs`) is unreliable on
  // iOS — focusing an input can push the sheet off-screen and leave the page
  // behind it at a drifted scroll offset (vaul#503/#619, DEV-6844). It's
  // disabled on the Drawer below; instead, pin the sheet to the visual
  // viewport ourselves so it stays above the software keyboard.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!(isDrawerOpen && viewport)) {
      return;
    }

    // Vaul mounts the drawer content a render after `open` flips, so the ref
    // is still null when this effect runs and must be read lazily. Track the
    // node each reposition styles so cleanup resets that same node.
    let styledContent: HTMLDivElement | null = null;
    const reposition = () => {
      const content = drawerContentRef.current;
      if (!content) {
        return;
      }
      styledContent = content;

      // How far the keyboard (plus any visual-viewport pan) intrudes into the
      // layout viewport that `fixed` elements are anchored to. Pinch-zoom
      // also shrinks the visual viewport, so ignore it as a keyboard signal.
      const isZoomed = viewport.scale > PINCH_ZOOM_SCALE_THRESHOLD;
      const keyboardInset = isZoomed ? 0 : Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      content.style.bottom = `${keyboardInset}px`;
      content.style.maxHeight = keyboardInset > 0 ? `${viewport.height - DRAWER_KEYBOARD_TOP_GAP}px` : '';
      if (keyboardInset > 0) {
        const active = document.activeElement;
        if (active instanceof HTMLElement && content.contains(active)) {
          active.scrollIntoView({
            block: 'nearest'
          });
        }
      }
    };
    viewport.addEventListener('resize', reposition);
    viewport.addEventListener('scroll', reposition);
    // Sync immediately in case the keyboard is already open when the drawer
    // opens. The content may not be mounted yet on this first call — the
    // next viewport event picks it up.
    reposition();
    return () => {
      viewport.removeEventListener('resize', reposition);
      viewport.removeEventListener('scroll', reposition);
      // Reset before the close animation so the sheet slides fully off-screen
      // even if the keyboard was still open when the drawer closed.
      if (styledContent) {
        styledContent.style.bottom = '';
        styledContent.style.maxHeight = '';
      }
    };
  }, [isDrawerOpen]);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShow(true);
    }, delay);
    return () => clearTimeout(timeoutId);
  }, [delay]);
  useEffect(() => () => clearTimeout(dismissTimeoutRef.current), []);
  const dismiss = useCallback(() => {
    if (dismissTimeoutRef.current) {
      return;
    }
    setIsExiting(true);
    // Close the drawer before unmounting so Vaul can restore body styles
    setIsDrawerOpen(false);
    dismissTimeoutRef.current = setTimeout(() => {
      setShow(false);
      if (dismissPermanently) {
        setAsDismissedInStorage({
          key: storageKey,
          storage: storageType
        });
      }
      onClickDismiss?.();
    }, DISMISS_ANIMATION_DURATION_MS);
  }, [onClickDismiss, storageKey, storageType, dismissPermanently]);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      const isForThisDialog = !event.data.storageKey || event.data.storageKey === storageKey;
      if (event.data.type === 'DISMISS_DIALOGUE' && isForThisDialog) {
        dismiss();
      }
      if (event.data.type === 'OPEN_DIALOGUE_DRAWER') {
        setIsDrawerOpen(true);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [dismiss, storageKey]);
  useEffect(() => {
    if (show) {
      onShow?.();
    }
  }, [show, onShow]);
  const isMobileVariant = !isMd && !!mobileChildren;
  if (isMobileVariant && !show) {
    return null;
  }
  const popInVariants: Variants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 50
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 20,
        duration: 0.15
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.15
      }
    }
  };
  if (isMobileVariant) {
    return <>
        <MobileBanner anyPlayerActive={anyPlayerActive} containerClassName="rounded-lg border border-gray-150 dark:border-gray-800" isExiting={isExiting} noXIcon onDismiss={dismiss} popInVariants={popInVariants} show={show}>
          {mobileChildren}
        </MobileBanner>

        <Drawer onOpenChange={open => {
        setIsDrawerOpen(open);
        // Closing the drawer dismisses the banner for good.
        if (!open) {
          dismiss();
        }
      }} open={isDrawerOpen} repositionInputs={false}>
          <DrawerContent className={cn('overflow-hidden p-0', drawerContentClassName)} ref={drawerContentRef}>
            {!!image && <div className="relative h-45 w-full">
                <Image alt={image.alt} className="object-cover" fill src={image.src} />
              </div>}

            <div className="min-h-0 overflow-y-auto p-5 pb-12">
              {drawerChildren}
            </div>
          </DrawerContent>
        </Drawer>
      </>;
  }

  // Stays mounted after `show` flips false so Radix can finish the exit
  // animation before unmounting the content.
  return <Dialog onOpenChange={open => {
    if (!open) {
      dismiss();
    }
  }} open={show && !isExiting} data-sentry-element="Dialog" data-sentry-component="PageDialog" data-sentry-source-file="PageDialog.tsx">
      <DialogPortal data-sentry-element="DialogPortal" data-sentry-source-file="PageDialog.tsx">
        <DialogOverlay className="bg-black/50 dark:bg-black/70" data-page-dialog data-sentry-element="DialogOverlay" data-sentry-source-file="PageDialog.tsx" />
        <DialogPrimitive.Content aria-describedby={undefined} className={cn('fixed top-1/2 left-1/2 z-50 w-[calc(100%-2rem)] max-w-[480px]', 'translate-x-[-50%] translate-y-[-50%]', 'overflow-hidden rounded-xl outline-none', 'bg-white dark:bg-gray-950', 'shadow-[0px_0px_15px_1px_rgba(35,37,37,0.12)] dark:shadow-[0px_0px_15px_1px_rgba(0,0,0,0.4)]', 'border border-gray-150 border-solid dark:border-gray-800', 'duration-200 data-[state=closed]:animate-out data-[state=open]:animate-in', 'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0', 'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95', 'data-[state=closed]:slide-out-to-bottom-2 data-[state=open]:slide-in-from-bottom-2', panelClassName)} data-page-dialog onOpenAutoFocus={event => {
        // Focus the panel itself rather than its first control, so the
        // X button doesn't open with a focus ring.
        event.preventDefault();
        const content = event.currentTarget;
        if (content instanceof HTMLElement) {
          content.focus({
            preventScroll: true
          });
        }
      }} data-sentry-element="DialogPrimitive.Content" data-sentry-source-file="PageDialog.tsx">
          <DialogTitle className="sr-only" data-sentry-element="DialogTitle" data-sentry-source-file="PageDialog.tsx">{title}</DialogTitle>

          {!noXIcon && <DialogClose asChild>
              <Button className="absolute top-2.5 right-4 z-10" size="icon" type="button" variant={image ? 'contrast' : 'transparent'}>
                <span className="sr-only">dismiss dialogue</span>
                <XIcon className="h-5 w-5" />
              </Button>
            </DialogClose>}

          {!!image && <div className="relative w-full" style={{
          height: DIALOG_IMAGE_HEIGHT
        }}>
              <div className="absolute inset-0 overflow-hidden">
                <Image alt={image.alt} className="object-cover" fill sizes="(max-width: 768px) 100vw, 480px" src={image.src} />
              </div>
              {!!image.badge && <Badge className="absolute bottom-0 left-1/2 z-10 w-fit -translate-x-1/2 translate-y-1/2 rounded-sm">
                  {image.badge}
                </Badge>}
            </div>}

          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>;
};